import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { get, onValue, push, ref, update as updateRealtimeData } from 'firebase/database';
import { doc, updateDoc } from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Clock3, LockKeyhole, Plus, SportShoe, Timer, Trash2, UserPlus, X } from 'lucide-react';
import { db, realtimeDb } from '../firebase/config';
import { useTeams } from '../teams/TeamProvider';
import { useAccess } from '../access/AccessProvider';
import { useAuth } from '../auth/AuthProvider';
import { AthleteSelectionDialog } from '../roster/AthleteSelectionDialog';
import { TimedResultDialog } from './TimedResultDialog';
import { ResultCommentDialog } from './ResultCommentDialog';
import { DeleteEventResultDialog } from './DeleteEventResultDialog';
import { HorizontalFieldResultsGrid } from './HorizontalFieldResultsGrid';
import { TimedResultsGrid } from './TimedResultsGrid';
import { VerticalFieldResultsGrid } from './VerticalFieldResultsGrid';
import { MultiEventResultsGrid } from './MultiEventResultsGrid';
import { RelayEditDialog } from './RelayEditDialog';
import { RelayResultsGrid } from './RelayResultsGrid';
import { deleteEventResult, saveIndividualResult, saveRelayResult, seedForResult } from './resultDatabase';
const heightEvents = new Set(['pole vault', 'high jump']);
const distanceEvents = new Set(['long jump', 'triple jump', 'shot put', 'discus', 'javelin', 'hammer', 'weight']);
const pointsEvents = new Set(['pentathlon', 'pentathlon (indoor)', 'pentathlon (outdoor)', 'heptathlon', 'decathlon']);
const trialFinalEvents = new Set(['50m', '50h', '55m', '55h', '60m', '60h', '100m', '100h', '110h', '200m', '200h', '300m', '300h', '400m', '400h', '500m']);
const splitEvents = new Set([
    '600m',
    '800m',
    '1000m',
    '1500m',
    '1600m',
    '1 mile',
    '2000m steeplechase',
    '3000m',
    '3000m steeplechase',
    '3200m',
    '2 mile',
    '5000m',
    '10000m',
    '1500m walk',
    '1600m walk',
    '3000m walk',
    '5000m walk',
]);
const eventsTimedInSeconds = new Set([
    '50m',
    '55m',
    '50h',
    '55h',
    '60m',
    '60h',
    '100m',
    '100h',
    '110h',
    '200m',
    '200h',
    '300m',
    '300h',
    '400m',
    '400h',
    '500m',
    '4x100m',
    '4x55h',
    '4x60h',
    '4x100h',
    '4x110h',
]);
const quarterInchEvents = new Set(['long jump', 'triple jump', 'shot put', 'weight']);
function eventResultType(name) {
    const normalized = name.toLowerCase();
    if (heightEvents.has(normalized))
        return 'Height';
    if (distanceEvents.has(normalized))
        return 'Distance';
    if (pointsEvents.has(normalized))
        return 'Points';
    return 'Time';
}
function displayEventName(eventName) {
    return eventName.replace(/^Pentathlon\s*\((?:Indoor|Outdoor)\)$/i, 'Pentathlon');
}
function athleteName(athlete) {
    return athlete ? [athlete.lastName, athlete.firstName].filter(Boolean).join(', ') : 'Athlete unavailable';
}
function athleteDialogName(athlete) {
    return athlete ? [athlete.firstName, athlete.lastName].filter(Boolean).join(' ') : 'Athlete unavailable';
}
function formatResult(value, eventName, unit) {
    if (value == null)
        return '-';
    if (unit !== 'imperial' || (!heightEvents.has(eventName.toLowerCase()) && !distanceEvents.has(eventName.toLowerCase())))
        return value;
    const match = String(value)
        .trim()
        .match(/^(\d+)-(\d{1,2}(?:\.\d+)?)$/);
    return match ? `${Number(match[1])}'-${Number(match[2])}\"` : value;
}
function resultValue(value) {
    if (typeof value === 'number')
        return value;
    if (!value)
        return Number.NaN;
    const normalized = String(value).trim();
    if (normalized.includes(':')) {
        const parts = normalized.split(':').map(Number);
        if (parts.every(Number.isFinite))
            return parts.reduce((total, part) => total * 60 + part, 0);
    }
    const imperial = normalized.match(/^(\d+)\s*(?:-|')\s*(\d+(?:\.\d+)?)/);
    if (imperial)
        return Number(imperial[1]) * 12 + Number(imperial[2]);
    return Number.parseFloat(normalized.replace(/[^\d.-]/g, ''));
}
function attemptEntries(attempts) {
    if (!attempts)
        return [];
    if (Array.isArray(attempts))
        return attempts.map((value, index) => [String(index), value]);
    if (typeof attempts === 'object')
        return Object.entries(attempts);
    return [['0', attempts]];
}
function attemptValue(value) {
    if (value && typeof value === 'object') {
        const entry = value;
        return entry.result ?? entry.attempts ?? entry.value ?? entry.mark ?? '-';
    }
    return value == null ? '-' : String(value);
}
function heightAttemptEntries(attempts) {
    return attemptEntries(attempts).map(([key, value]) => {
        const encoded = String(attemptValue(value));
        if (!encoded.includes('%'))
            return { height: key, attempts: encoded };
        const [height, ...marks] = encoded.split('%');
        return { height, attempts: marks.filter(Boolean).join('') };
    });
}
function isRelayEvent(name) {
    return /^4x/i.test(name.trim()) || /relay/i.test(name);
}
function scoreEvent(event) {
    const first = Number(event.firstPlace ?? -1);
    const second = Number(event.secondPlace ?? -1);
    const third = Number(event.thirdPlace ?? -1);
    const total = { mine: 0, opponent: 0 };
    const add = (owner, points) => {
        if (owner === 1)
            total.mine += points;
        if (owner === 2)
            total.opponent += points;
        if (owner === 0) {
            total.mine += points / 2;
            total.opponent += points / 2;
        }
    };
    if (first === -1)
        return total;
    if (isRelayEvent(String(event.name ?? ''))) {
        add(first, 5);
        return total;
    }
    if (first === 0) {
        total.mine += 4;
        total.opponent += 4;
        add(third, 1);
        return total;
    }
    add(first, 5);
    if (second === 0) {
        total.mine += 2;
        total.opponent += 2;
        return total;
    }
    add(second, 3);
    add(third, 1);
    return total;
}
function formatScore(value) {
    return Number.isInteger(value) ? String(value) : value.toFixed(1);
}
function multiSubevents(name, indoor, male, open) {
    const normalized = name.toLowerCase();
    if (normalized.includes('decathlon'))
        return male
            ? ['100m', 'Long Jump', 'Shot Put', 'High Jump', '400m', '110H', 'Discus', 'Pole Vault', 'Javelin', '1500m']
            : ['100m', 'Discus', 'Pole Vault', 'Javelin', '400m', '100H', 'Long Jump', 'Shot Put', 'High Jump', '1500m'];
    if (normalized.includes('heptathlon'))
        return male
            ? ['60m', 'Long Jump', 'Shot Put', 'High Jump', '60H', 'Pole Vault', '1000m']
            : ['100H', 'High Jump', 'Shot Put', '200m', 'Long Jump', 'Javelin', '800m'];
    if (indoor)
        return male ? ['60H', 'Long Jump', 'Shot Put', 'High Jump', '1000m'] : ['60H', 'High Jump', 'Shot Put', 'Long Jump', '800m'];
    if (male && open)
        return ['Long Jump', 'Javelin', '200m', 'Discus', '1500m'];
    return male ? ['110H', 'Shot Put', 'High Jump', 'Long Jump', '1500m'] : ['100H', 'Shot Put', 'High Jump', 'Long Jump', '800m'];
}
export function EventDetailsPage({ meet, event, onBack, publicContext, }) {
    const { athletes, selectedTeam: activeTeam, selectedSeason: activeSeason } = useTeams();
    const selectedTeam = publicContext?.team ?? activeTeam;
    const selectedSeason = publicContext?.season ?? activeSeason;
    const { canWriteSeason, seasonAccess } = useAccess();
    const { hasSubscriptionAccess, subscriptionRequiredMessage } = useAuth();
    const [eventData, setEventData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState('');
    const [timeOpen, setTimeOpen] = useState(false);
    const [timeValue, setTimeValue] = useState('');
    const [timeSaving, setTimeSaving] = useState(false);
    const [timeError, setTimeError] = useState('');
    const [athleteSelectorOpen, setAthleteSelectorOpen] = useState(false);
    const [addingAthletes, setAddingAthletes] = useState(false);
    const [athleteSelectorError, setAthleteSelectorError] = useState('');
    const [addHeightRequest, setAddHeightRequest] = useState(0);
    const [expandedResultId, setExpandedResultId] = useState(null);
    const [timedResultDialog, setTimedResultDialog] = useState(null);
    const [commentResult, setCommentResult] = useState(null);
    const [deleteResult, setDeleteResult] = useState(null);
    const [relayResult, setRelayResult] = useState(null);
    const canWriteEvent = !publicContext && canWriteSeason(selectedSeason?.id);
    const canManageEvent = hasSubscriptionAccess && canWriteEvent;
    const seasonLocked = !publicContext && seasonAccess(selectedSeason?.id)?.locked === true;
    const subscriptionTitle = hasSubscriptionAccess ? undefined : subscriptionRequiredMessage;
    const dualMeet = meet.type === 'Dual Meet' || meet.type === 'Double Dual Meet' || meet.type === 'Triple Dual Meet';
    const crossCountry = selectedSeason?.name.trim().toLowerCase() === 'cross country' || Boolean(meet.course);
    const handTimeForResult = (result) => result.hand ?? (result.result == null && result.times?.trial == null && result.times?.final == null ? dualMeet : false);
    useEffect(() => {
        if (!selectedTeam || !selectedSeason)
            return;
        setLoading(true);
        setError('');
        return onValue(ref(realtimeDb, `competitions/${selectedTeam.id}/${selectedSeason.id}/results/${meet.id}/${event.id}`), (snapshot) => {
            setEventData(snapshot.val());
            setLoading(false);
        }, (caughtError) => {
            setError(caughtError.message);
            setLoading(false);
        });
    }, [event.id, meet.id, selectedSeason, selectedTeam]);
    async function deleteEvent() {
        if (!selectedTeam || !selectedSeason || !canWriteEvent)
            return;
        setDeleting(true);
        setDeleteError('');
        try {
            const competitionPath = `competitions/${selectedTeam.id}/${selectedSeason.id}`;
            const eventSnapshot = await get(ref(realtimeDb, `${competitionPath}/results/${meet.id}/${event.id}`));
            let hasResults = false;
            eventSnapshot.child('results').forEach((resultSnapshot) => {
                if (resultSnapshot.child('result').exists()) {
                    hasResults = true;
                    return true;
                }
                return false;
            });
            if (hasResults) {
                setDeleteError('This event contains athlete results and cannot be deleted.');
                return;
            }
            const realtimeUpdates = { [`${competitionPath}/results/${meet.id}/${event.id}`]: null };
            const firestoreScores = {};
            if (dualMeet && !crossCountry) {
                for (const opponentKey of Object.keys(meet.opponent ?? {})) {
                    const scoreSnapshot = await get(ref(realtimeDb, `${competitionPath}/scores/${meet.id}/${opponentKey}`));
                    const total = { mine: 0, opponent: 0 };
                    scoreSnapshot.forEach((scoreEventSnapshot) => {
                        if (scoreEventSnapshot.key === event.id)
                            return false;
                        const points = scoreEvent((scoreEventSnapshot.val() ?? {}));
                        total.mine += points.mine;
                        total.opponent += points.opponent;
                        return false;
                    });
                    realtimeUpdates[`${competitionPath}/scores/${meet.id}/${opponentKey}/${event.id}`] = null;
                    firestoreScores[`score.${opponentKey}`] = `${formatScore(total.mine)} - ${formatScore(total.opponent)}`;
                }
            }
            await updateRealtimeData(ref(realtimeDb), realtimeUpdates);
            if (Object.keys(firestoreScores).length > 0)
                await updateDoc(doc(db, 'teams', selectedTeam.id, 'schedule', meet.id), firestoreScores);
            setDeleteOpen(false);
            onBack();
        }
        catch (caughtError) {
            setDeleteError(caughtError instanceof Error ? caughtError.message : 'The event could not be deleted.');
        }
        finally {
            setDeleting(false);
        }
    }
    const eventName = eventData?.name ?? event.name;
    const resultType = eventResultType(eventName);
    const results = useMemo(() => Object.entries(eventData?.results ?? {})
        .map(([id, result]) => ({ ...result, id: result.id ?? id }))
        .sort((left, right) => {
        const leftValue = resultValue(left.result ?? left.times?.final);
        const rightValue = resultValue(right.result ?? right.times?.final);
        if (!Number.isFinite(leftValue) && !Number.isFinite(rightValue))
            return Number(left.seed ?? Number.MAX_SAFE_INTEGER) - Number(right.seed ?? Number.MAX_SAFE_INTEGER);
        if (!Number.isFinite(leftValue))
            return 1;
        if (!Number.isFinite(rightValue))
            return -1;
        if (resultType !== 'Time')
            return rightValue - leftValue;
        const leftHand = left.hand === true;
        const rightHand = right.hand === true;
        const difference = leftValue + (leftHand ? 0.24 : 0) - (rightValue + (rightHand ? 0.24 : 0));
        return difference || Number(leftHand) - Number(rightHand);
    }), [dualMeet, eventData, resultType]);
    const relayEvent = isRelayEvent(eventName);
    const multiEvent = pointsEvents.has(eventName.toLowerCase());
    const normalizedEventName = eventName.trim().toLowerCase();
    const horizontalFieldEvent = distanceEvents.has(normalizedEventName);
    const verticalFieldEvent = heightEvents.has(normalizedEventName);
    const sprintGridEvent = trialFinalEvents.has(normalizedEventName) && !relayEvent;
    const splitGridEvent = splitEvents.has(normalizedEventName) && !relayEvent;
    const multiEventOrder = multiSubevents(eventName, selectedSeason?.name.trim().toLowerCase() === 'indoor', selectedTeam?.gender === 'male', selectedTeam?.level === 'open');
    const multiDisplayOrder = multiEventOrder.map((name) => name.toLowerCase());
    function actionsForResult() {
        if (multiEvent)
            return ['Notes', 'Delete'];
        if (relayEvent)
            return ['Edit Relay', 'Notes', 'Delete'];
        if (heightEvents.has(normalizedEventName))
            return ['Enter Height', 'Attempts', 'Notes', 'Delete'];
        if (distanceEvents.has(normalizedEventName))
            return ['Attempts', 'Notes', 'Delete'];
        if (trialFinalEvents.has(normalizedEventName))
            return ['Enter Trial', 'Enter Final', 'Notes', 'Delete'];
        if (splitEvents.has(normalizedEventName))
            return ['Enter Time', 'Splits', 'Notes', 'Delete'];
        return ['Notes', 'Delete'];
    }
    function handleResultAction(action, result) {
        if (!canWriteEvent || (!canManageEvent && action !== 'Delete'))
            return;
        if (action === 'Edit Relay') {
            setRelayResult(result);
            return;
        }
        if (action === 'Delete') {
            setDeleteResult(result);
            return;
        }
        if (action === 'Notes') {
            setCommentResult(result);
            return;
        }
        const kind = action === 'Enter Trial' ? 'trial' : action === 'Enter Final' ? 'final' : action === 'Enter Time' ? 'time' : null;
        if (kind)
            setTimedResultDialog({ result, kind });
    }
    function databaseContext(fieldEvent = false) {
        if (!selectedTeam || !selectedSeason)
            throw new Error('Select a team and season before updating results.');
        return {
            teamId: selectedTeam.id,
            seasonId: selectedSeason.id,
            meetId: meet.id,
            meetName: meet.name,
            meetDateTime: meet.date.getTime(),
            meetLocation: meet.location,
            meetCourse: meet.course,
            eventId: event.id,
            eventName,
            fieldEvent,
        };
    }
    async function updateIndividualResult(resultId, patch, fieldEvent = false) {
        if (!canManageEvent)
            return;
        const current = results.find((result) => result.id === resultId);
        if (!current)
            throw new Error('The result is no longer available.');
        await saveIndividualResult(databaseContext(fieldEvent), { ...current, ...patch, id: resultId, name: eventName });
    }
    async function updateEventHeights(heights) {
        if (!selectedTeam || !selectedSeason || !canManageEvent)
            return;
        await updateRealtimeData(ref(realtimeDb, `competitions/${selectedTeam.id}/${selectedSeason.id}/results/${meet.id}/${event.id}`), { heights });
    }
    async function updateTimedResult(result, value) {
        const trial = value.kind === 'trial' ? value.result : result.times?.trial == null ? null : String(result.times.trial);
        const implicitFinal = value.kind === 'trial' && result.times?.trial == null && result.times?.final == null && result.result != null ? String(result.result) : null;
        const final = value.kind === 'final' ? value.result : result.times?.final == null ? implicitFinal : String(result.times.final);
        let best = value.kind === 'time' ? value.result : (final ?? trial);
        if (trial && final)
            best = (seedForResult(trial) ?? 99999) <= (seedForResult(final) ?? 99999) ? trial : final;
        const times = trial ? { trial, ...(final ? { final } : {}) } : null;
        await updateIndividualResult(result.id ?? '', { result: best, seed: seedForResult(best), hand: value.hand, times });
    }
    const eligibleAthletes = useMemo(() => {
        const enteredIds = new Set();
        results.forEach((result) => {
            if (result.athlete?.id)
                enteredIds.add(result.athlete.id);
            Object.values(result.relay ?? {}).forEach((leg) => {
                if (leg.athlete?.id)
                    enteredIds.add(leg.athlete.id);
            });
        });
        return athletes.filter((athlete) => !enteredIds.has(athlete.id));
    }, [athletes, results]);
    async function addAthletesToEvent(selectedAthletes) {
        if (!selectedTeam || !selectedSeason || !canManageEvent || selectedAthletes.length === 0)
            return;
        if (relayEvent && selectedAthletes.length !== 4)
            return setAthleteSelectorError('Select four athletes for a relay.');
        setAddingAthletes(true);
        setAthleteSelectorError('');
        try {
            const resultsRef = ref(realtimeDb, `competitions/${selectedTeam.id}/${selectedSeason.id}/results/${meet.id}/${event.id}/results`);
            const updates = {};
            const athleteValue = (athlete) => ({ id: athlete.id, firstName: athlete.firstName, lastName: athlete.lastName });
            if (relayEvent) {
                const resultRef = push(resultsRef);
                const id = resultRef.key;
                updates[id] = {
                    id,
                    name: eventName,
                    relay: Object.fromEntries(selectedAthletes.map((athlete, index) => [`leg${index + 1}`, { athlete: athleteValue(athlete) }])),
                };
            }
            else {
                const indoor = selectedSeason.name.trim().toLowerCase() === 'indoor';
                const male = selectedTeam.gender === 'male';
                const open = selectedTeam.level === 'open';
                const multi = multiEvent
                    ? Object.fromEntries(multiSubevents(eventName, indoor, male, open).map((name) => [name, { name, points: 0 }]))
                    : undefined;
                selectedAthletes.forEach((athlete) => {
                    const resultRef = push(resultsRef);
                    const id = resultRef.key;
                    updates[id] = { id, name: eventName, athlete: athleteValue(athlete), ...(multi ? { multi } : {}) };
                });
            }
            await updateRealtimeData(resultsRef, updates);
            setAthleteSelectorOpen(false);
        }
        catch (caughtError) {
            setAthleteSelectorError(caughtError instanceof Error ? caughtError.message : 'The athletes could not be added.');
        }
        finally {
            setAddingAthletes(false);
        }
    }
    function openTimeDialog() {
        const currentTime = eventData?.time == null ? null : new Date(eventData.time);
        setTimeValue(currentTime && Number.isFinite(currentTime.getTime())
            ? `${String(currentTime.getHours()).padStart(2, '0')}:${String(currentTime.getMinutes()).padStart(2, '0')}`
            : '');
        setTimeError('');
        setTimeOpen(true);
    }
    async function saveEventTime() {
        if (!selectedTeam || !selectedSeason || !canManageEvent || !timeValue)
            return setTimeError('Select an event time.');
        const [hours, minutes] = timeValue.split(':').map(Number);
        const eventDate = new Date(meet.date);
        eventDate.setHours(hours, minutes, 0, 0);
        if (!Number.isFinite(eventDate.getTime()))
            return setTimeError('Select a valid event time.');
        setTimeSaving(true);
        setTimeError('');
        try {
            await updateRealtimeData(ref(realtimeDb, `competitions/${selectedTeam.id}/${selectedSeason.id}/results/${meet.id}/${event.id}`), {
                time: eventDate.getTime(),
            });
            setTimeOpen(false);
        }
        catch (caughtError) {
            setTimeError(caughtError instanceof Error ? caughtError.message : 'The event time could not be saved.');
        }
        finally {
            setTimeSaving(false);
        }
    }
    async function removeEventTime() {
        if (!selectedTeam || !selectedSeason || !canWriteEvent)
            return;
        setTimeSaving(true);
        setTimeError('');
        try {
            await updateRealtimeData(ref(realtimeDb, `competitions/${selectedTeam.id}/${selectedSeason.id}/results/${meet.id}/${event.id}`), { time: null });
            setTimeOpen(false);
        }
        catch (caughtError) {
            setTimeError(caughtError instanceof Error ? caughtError.message : 'The event time could not be removed.');
        }
        finally {
            setTimeSaving(false);
        }
    }
    return (_jsxs("div", { className: "content event-details-page", children: [_jsxs("button", { className: "back-button", type: "button", onClick: onBack, children: [_jsx(ArrowLeft, { size: 17 }), " Back to meet results"] }), _jsxs("section", { className: "event-details-hero", children: [_jsxs("div", { children: [_jsx("span", { children: meet.name }), _jsx("h1", { children: displayEventName(eventName) })] }), _jsxs("div", { className: "event-details-hero__side", children: [eventData?.time != null && (_jsxs("span", { className: "event-start-time", children: [_jsx(Clock3, { size: 14 }), new Date(eventData.time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })] })), _jsxs("div", { className: "event-detail-actions", children: [canWriteEvent && (_jsxs("button", { type: "button", disabled: !hasSubscriptionAccess, title: subscriptionTitle, onClick: () => {
                                            if (!hasSubscriptionAccess)
                                                return;
                                            setAthleteSelectorError('');
                                            setAthleteSelectorOpen(true);
                                        }, children: [_jsx(UserPlus, { size: 16 }), " Add Athlete(s)"] })), canWriteEvent && verticalFieldEvent && (_jsxs("button", { type: "button", disabled: !hasSubscriptionAccess, title: subscriptionTitle, onClick: () => {
                                            if (hasSubscriptionAccess)
                                                setAddHeightRequest((current) => current + 1);
                                        }, children: [_jsx(Plus, { size: 16 }), " Add Height"] })), canWriteEvent && (_jsxs("button", { type: "button", disabled: !hasSubscriptionAccess && eventData?.time == null, title: !hasSubscriptionAccess && eventData?.time == null ? subscriptionTitle : undefined, onClick: openTimeDialog, children: [_jsx(Clock3, { size: 16 }), " Event Time"] })), canWriteEvent && (_jsxs("button", { className: "event-delete-button", type: "button", onClick: () => {
                                            setDeleteError('');
                                            setDeleteOpen(true);
                                        }, children: [_jsx(Trash2, { size: 16 }), " Delete Event"] }))] })] })] }), seasonLocked && (_jsxs("div", { className: "season-locked-notice", role: "status", children: [_jsx(LockKeyhole, { size: 18 }), _jsxs("div", { children: [_jsx("strong", { children: "Season locked" }), _jsx("span", { children: "This season is completed. Event results are view-only and cannot be edited." })] })] })), loading && (_jsxs("div", { className: "details-state", children: [_jsx(Timer, { size: 24 }), _jsx("strong", { children: "Loading event results..." })] })), !loading && error && (_jsxs("div", { className: "details-state details-state--error", children: [_jsx("strong", { children: "Event results could not be loaded" }), _jsx("span", { children: "Access denied" })] })), !loading && !error && results.length === 0 && (_jsxs("div", { className: "details-state", children: [_jsx(SportShoe, { size: 24 }), _jsx("strong", { children: "No entries" }), _jsxs("span", { children: ["Add athletes to the ", event.name, "."] })] })), !loading && !error && results.length > 0 && relayEvent && (_jsx(RelayResultsGrid, { eventName: eventName, dualMeet: dualMeet, readOnly: !canManageEvent, showActions: !publicContext, canDelete: canWriteEvent, disabledTitle: subscriptionTitle, rows: results, onEdit: (id) => setRelayResult(results.find((result) => result.id === id) ?? null), onNotes: (id) => setCommentResult(results.find((result) => result.id === id) ?? null), onDelete: (id) => setDeleteResult(results.find((result) => result.id === id) ?? null) })), !loading &&
                !error &&
                results.length > 0 &&
                (horizontalFieldEvent ? (_jsx(HorizontalFieldResultsGrid, { readOnly: !canManageEvent, showActions: !publicContext, canDelete: canWriteEvent, disabledTitle: subscriptionTitle, eventName: displayEventName(eventName), metric: selectedTeam?.unit === 'metric', quarterInches: quarterInchEvents.has(normalizedEventName), rows: results.map((result) => ({
                        id: result.id ?? '',
                        displayName: athleteName(result.athlete),
                        dialogName: athleteDialogName(result.athlete),
                        comment: result.comment,
                        attempts: attemptEntries(result.attempts).map(([, value]) => attemptValue(value)),
                        fallbackResult: result.result,
                        fallbackSeed: result.seed,
                    })), onNotes: (resultId) => setCommentResult(results.find((result) => result.id === resultId) ?? null), onDelete: (resultId) => setDeleteResult(results.find((result) => result.id === resultId) ?? null), onUpdate: (resultId, value) => updateIndividualResult(resultId, value, true) })) : verticalFieldEvent ? (_jsx(VerticalFieldResultsGrid, { readOnly: !canManageEvent, showActions: !publicContext, canDelete: canWriteEvent, disabledTitle: subscriptionTitle, eventName: displayEventName(eventName), metric: selectedTeam?.unit === 'metric', eventHeights: eventData?.heights, addHeightRequest: addHeightRequest, rows: results.map((result) => ({
                        id: result.id ?? '',
                        displayName: athleteName(result.athlete),
                        dialogName: athleteDialogName(result.athlete),
                        comment: result.comment,
                        result: result.result,
                        seed: result.seed,
                        attempts: attemptEntries(result.attempts).map(([, value]) => attemptValue(value)),
                    })), onNotes: (resultId) => setCommentResult(results.find((result) => result.id === resultId) ?? null), onDelete: (resultId) => setDeleteResult(results.find((result) => result.id === resultId) ?? null), onHeightsUpdate: updateEventHeights, onUpdate: (resultId, value) => updateIndividualResult(resultId, value, true) })) : multiEvent ? (_jsx(MultiEventResultsGrid, { readOnly: !canManageEvent, showActions: !publicContext, canDelete: canWriteEvent, disabledTitle: subscriptionTitle, eventName: displayEventName(eventName), eventOrder: multiEventOrder, isMale: selectedTeam?.gender === 'male', rows: results.map((result) => ({
                        id: result.id ?? '',
                        displayName: athleteName(result.athlete),
                        dialogName: athleteDialogName(result.athlete),
                        comment: result.comment,
                        result: result.result,
                        multi: result.multi,
                    })), onNotes: (resultId) => setCommentResult(results.find((result) => result.id === resultId) ?? null), onDelete: (resultId) => setDeleteResult(results.find((result) => result.id === resultId) ?? null), onUpdate: (resultId, value) => updateIndividualResult(resultId, value) })) : crossCountry ? (_jsx(TimedResultsGrid, { readOnly: !canManageEvent, showActions: !publicContext, canDelete: canWriteEvent, disabledTitle: subscriptionTitle, eventName: displayEventName(eventName), mode: "race", indoor: false, timedInSeconds: false, defaultHand: dualMeet, rows: results.map((result) => ({
                        id: result.id ?? '',
                        displayName: athleteName(result.athlete),
                        dialogName: athleteDialogName(result.athlete),
                        comment: result.comment,
                        result: result.result,
                        seed: result.seed,
                        hand: result.hand,
                        dualMeet,
                    })), onNotes: (resultId) => setCommentResult(results.find((result) => result.id === resultId) ?? null), onDelete: (resultId) => setDeleteResult(results.find((result) => result.id === resultId) ?? null), onUpdate: (resultId, value) => updateIndividualResult(resultId, value) })) : sprintGridEvent || splitGridEvent ? (_jsx(TimedResultsGrid, { readOnly: !canManageEvent, showActions: !publicContext, canDelete: canWriteEvent, disabledTitle: subscriptionTitle, eventName: displayEventName(eventName), mode: sprintGridEvent ? 'sprint' : 'splits', indoor: selectedSeason?.name.trim().toLowerCase() === 'indoor', timedInSeconds: eventsTimedInSeconds.has(normalizedEventName), defaultHand: dualMeet, rows: results.map((result) => ({
                        id: result.id ?? '',
                        displayName: athleteName(result.athlete),
                        dialogName: athleteDialogName(result.athlete),
                        comment: result.comment,
                        result: result.result,
                        seed: result.seed,
                        hand: result.hand,
                        dualMeet,
                        times: result.times,
                        splits: Array.isArray(result.splits) ? result.splits : Object.values(result.splits ?? {}),
                    })), onNotes: (resultId) => setCommentResult(results.find((result) => result.id === resultId) ?? null), onDelete: (resultId) => setDeleteResult(results.find((result) => result.id === resultId) ?? null), onUpdate: (resultId, value) => updateIndividualResult(resultId, value) })) : (_jsx("section", { className: "detailed-result-list", children: results.map((result) => {
                        const resultId = result.id ?? '';
                        const attempts = attemptEntries(result.attempts);
                        const heightAttempts = heightAttemptEntries(result.attempts);
                        const relay = Object.entries(result.relay ?? {}).sort(([left], [right]) => Number(left.replace(/\D/g, '')) - Number(right.replace(/\D/g, '')));
                        const multi = Object.entries(result.multi ?? {}).sort(([leftKey, left], [rightKey, right]) => {
                            const leftName = (left.name ?? leftKey).toLowerCase();
                            const rightName = (right.name ?? rightKey).toLowerCase();
                            const leftIndex = multiDisplayOrder.indexOf(leftName);
                            const rightIndex = multiDisplayOrder.indexOf(rightName);
                            if (leftIndex === -1 && rightIndex === -1)
                                return leftName.localeCompare(rightName);
                            if (leftIndex === -1)
                                return 1;
                            if (rightIndex === -1)
                                return -1;
                            return leftIndex - rightIndex;
                        });
                        return (_jsxs("article", { className: `detailed-result-card panel ${expandedResultId === resultId ? 'detailed-result-card--expanded' : ''}`, role: canWriteEvent ? 'button' : undefined, tabIndex: canWriteEvent ? 0 : undefined, onClick: () => {
                                if (canWriteEvent)
                                    setExpandedResultId((current) => (current === resultId ? null : resultId));
                            }, onKeyDown: (keyboardEvent) => {
                                if (canWriteEvent && (keyboardEvent.key === 'Enter' || keyboardEvent.key === ' '))
                                    setExpandedResultId((current) => (current === resultId ? null : resultId));
                            }, children: [_jsxs("div", { className: "detailed-result-card__header", children: [_jsxs("div", { children: [_jsx("strong", { children: result.team ||
                                                        (relay.length > 0 ? _jsx("em", { className: "result-placeholder", children: "Team name" }) : athleteName(result.athlete)) }), result.comment && _jsx("p", { children: result.comment })] }), _jsx("span", { className: result.result == null ? 'result-placeholder' : '', children: result.result == null ? resultType : formatResult(result.result, eventName, selectedTeam?.unit) })] }), result.times && (result.times.trial != null || result.times.final != null) && (_jsxs("div", { className: "time-breakdown", children: [result.times.trial != null && (_jsxs("span", { children: [_jsx("small", { children: "TRIAL" }), _jsx("strong", { children: result.times.trial })] })), result.times.final != null && (_jsxs("span", { children: [_jsx("small", { children: "FINAL" }), _jsx("strong", { children: result.times.final })] }))] })), attempts.length > 0 && distanceEvents.has(normalizedEventName) && (_jsxs("div", { className: "distance-attempts", children: [_jsx("span", { children: "ATTEMPTS" }), _jsx("div", { children: attempts.map(([key, value]) => (_jsx("strong", { children: formatResult(attemptValue(value), eventName, selectedTeam?.unit) }, key))) })] })), heightAttempts.length > 0 && heightEvents.has(eventName.toLowerCase()) && (_jsxs("div", { className: "height-attempts", children: [_jsx("span", { children: "HEIGHT PROGRESSION" }), heightAttempts.map(({ height, attempts: marks }, index) => (_jsxs("div", { children: [_jsx("strong", { children: formatResult(height, eventName, selectedTeam?.unit) }), _jsx("small", { children: marks.toUpperCase() })] }, `${height}-${index}`)))] })), relay.length > 0 && (_jsx("div", { className: "event-relay-details", children: relay.map(([leg, value]) => (_jsxs("div", { children: [_jsx("small", { children: leg.replace(/\D/g, '') }), _jsx("strong", { children: athleteName(value.athlete) }), value.split != null && _jsx("span", { children: value.split })] }, leg))) })), multi.length > 0 && (_jsx("div", { className: "event-multi-details", children: multi.map(([key, value]) => {
                                        const subeventName = value.name ?? key;
                                        return (_jsxs("div", { children: [_jsx("strong", { children: subeventName }), _jsx("span", { className: value.result == null ? 'result-placeholder' : '', children: value.result == null
                                                        ? eventResultType(subeventName)
                                                        : formatResult(value.result, subeventName, selectedTeam?.unit) }), _jsx("small", { children: value.points ?? 0 })] }, key));
                                    }) })), canWriteEvent && expandedResultId === resultId && (_jsx("div", { className: "result-action-bar", onClick: (clickEvent) => clickEvent.stopPropagation(), children: actionsForResult().map((action) => {
                                        const subscriptionDisabled = !hasSubscriptionAccess && action !== 'Delete';
                                        const disabled = subscriptionDisabled ||
                                            (action === 'Enter Final' &&
                                                String(result.times?.trial ?? '')
                                                    .trim()
                                                    .toUpperCase() === 'DQ');
                                        const hasSplits = action === 'Splits' && Object.keys(result.splits ?? {}).length > 0;
                                        const resultEntryAction = !['Notes', 'Delete', 'Edit Relay'].includes(action);
                                        const className = [
                                            action === 'Delete' ? 'result-action-bar__delete' : '',
                                            hasSplits ? 'result-action-bar__has-data' : '',
                                            resultEntryAction ? 'result-action-bar__entry-action' : '',
                                        ]
                                            .filter(Boolean)
                                            .join(' ');
                                        return (_jsx("button", { className: className, type: "button", disabled: disabled, title: subscriptionDisabled
                                                ? subscriptionTitle
                                                : disabled
                                                    ? 'A final cannot be entered after a disqualified trial.'
                                                    : hasSplits
                                                        ? 'Splits have been entered'
                                                        : undefined, onClick: () => handleResultAction(action, result), children: action }, action));
                                    }) }))] }, resultId));
                    }) }))), deleteOpen && (_jsx("div", { className: "profile-confirm-backdrop", role: "presentation", onMouseDown: () => {
                    if (!deleting)
                        setDeleteOpen(false);
                }, children: _jsxs("section", { className: "profile-confirm-dialog", role: "dialog", "aria-modal": "true", "aria-labelledby": "delete-event-title", onMouseDown: (event) => event.stopPropagation(), children: [_jsx("button", { className: "profile-confirm-close", type: "button", disabled: deleting, onClick: () => setDeleteOpen(false), "aria-label": "Close", children: _jsx(X, { size: 20 }) }), _jsxs("div", { className: "profile-confirm-content", children: [_jsx(Trash2, { size: 24 }), _jsxs("h2", { id: "delete-event-title", children: ["Delete ", displayEventName(eventName), "?"] }), _jsx("p", { children: "This permanently removes the event. Events containing athlete results cannot be deleted." }), deleteError && _jsx("span", { className: "profile-confirm-error", children: deleteError }), _jsxs("div", { children: [_jsx("button", { type: "button", disabled: deleting, onClick: () => setDeleteOpen(false), children: "Cancel" }), _jsx("button", { className: "profile-confirm-delete", type: "button", disabled: deleting || Boolean(deleteError), onClick: () => void deleteEvent(), children: deleting ? 'Checking...' : 'Delete event' })] })] })] }) })), timeOpen && (_jsx("div", { className: "event-time-backdrop", role: "presentation", onMouseDown: () => {
                    if (!timeSaving)
                        setTimeOpen(false);
                }, children: _jsxs("section", { className: "event-time-dialog", role: "dialog", "aria-modal": "true", "aria-labelledby": "event-time-title", onMouseDown: (event) => event.stopPropagation(), children: [_jsx("button", { className: "event-time-close", type: "button", disabled: timeSaving, onClick: () => setTimeOpen(false), "aria-label": "Close", children: _jsx(X, { size: 20 }) }), _jsxs("div", { className: "event-time-heading", children: [_jsx("span", { children: _jsx(Clock3, { size: 21 }) }), _jsxs("div", { children: [_jsx("h2", { id: "event-time-title", children: "Event Time" }), _jsxs("p", { children: [displayEventName(eventName), " - ", meet.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })] })] })] }), _jsxs("label", { children: ["Start time", _jsx("input", { type: "time", value: timeValue, disabled: !hasSubscriptionAccess, onChange: (event) => setTimeValue(event.target.value) })] }), timeError && _jsx("div", { className: "meet-form-error", children: timeError }), _jsxs("footer", { children: [eventData?.time != null && (_jsxs("button", { className: "event-time-remove", type: "button", disabled: timeSaving, onClick: () => void removeEventTime(), children: [_jsx(Trash2, { size: 15 }), " Remove time"] })), _jsx("span", {}), _jsx("button", { type: "button", disabled: timeSaving, onClick: () => setTimeOpen(false), children: "Cancel" }), _jsx("button", { type: "button", disabled: timeSaving || !hasSubscriptionAccess, title: subscriptionTitle, onClick: () => void saveEventTime(), children: timeSaving ? 'Saving...' : 'Save time' })] })] }) })), athleteSelectorOpen && (_jsx(AthleteSelectionDialog, { title: relayEvent ? 'Select Relay Athletes' : `Add Athletes to ${displayEventName(eventName)}`, description: relayEvent ? 'Select four athletes. Their selection order becomes legs 1 through 4.' : 'Select athletes from the current season roster.', athletes: eligibleAthletes, error: athleteSelectorError, minSelections: relayEvent ? 4 : 1, maxSelections: relayEvent ? 4 : undefined, showTypeFilters: true, confirmLabel: relayEvent ? 'Add relay team' : 'Add athletes', submitting: addingAthletes, onClose: () => {
                    if (!addingAthletes)
                        setAthleteSelectorOpen(false);
                }, onConfirm: (selectedAthletes) => void addAthletesToEvent(selectedAthletes) })), timedResultDialog &&
                (() => {
                    const implicitFinal = timedResultDialog.result.times?.trial == null ? timedResultDialog.result.result : undefined;
                    return (_jsx(TimedResultDialog, { athleteName: athleteDialogName(timedResultDialog.result.athlete), eventName: displayEventName(eventName), kind: timedResultDialog.kind, timedInSeconds: eventsTimedInSeconds.has(normalizedEventName), currentValue: timedResultDialog.kind === 'trial'
                            ? timedResultDialog.result.times?.trial
                            : timedResultDialog.kind === 'final'
                                ? (timedResultDialog.result.times?.final ?? implicitFinal)
                                : timedResultDialog.result.result, currentHand: handTimeForResult(timedResultDialog.result), disableDQ: timedResultDialog.kind === 'trial' && Boolean(String(timedResultDialog.result.times?.final ?? implicitFinal ?? '').trim()), onSubmit: (value) => void updateTimedResult(timedResultDialog.result, value).then(() => setTimedResultDialog(null)), onClose: () => setTimedResultDialog(null) }));
                })(), relayResult && (_jsx(RelayEditDialog, { eventName: displayEventName(eventName), teamName: relayResult.team, trial: relayResult.times?.trial, final: relayResult.times?.final, result: relayResult.result, hand: relayResult.hand ?? dualMeet, dualMeet: dualMeet, relay: relayResult.relay, athletes: athletes, onSave: async (value) => saveRelayResult(databaseContext(), { ...relayResult, ...value, id: relayResult.id, name: eventName }, value.removedAthleteIds), onClose: () => setRelayResult(null) })), commentResult && (_jsx(ResultCommentDialog, { athleteName: commentResult.team || athleteDialogName(commentResult.athlete), eventName: displayEventName(eventName), currentComment: commentResult.comment, onSave: async (comment) => relayEvent
                    ? saveRelayResult(databaseContext(), { ...commentResult, comment })
                    : updateIndividualResult(commentResult.id ?? '', { comment }, horizontalFieldEvent || verticalFieldEvent), onClose: () => setCommentResult(null) })), deleteResult && (_jsx(DeleteEventResultDialog, { athleteName: deleteResult.team || athleteDialogName(deleteResult.athlete), eventName: displayEventName(eventName), hasResult: deleteResult.result != null && String(deleteResult.result).trim() !== '', onConfirm: async () => deleteEventResult(databaseContext(), deleteResult), onClose: () => setDeleteResult(null) }))] }));
}
